/*----------------------------------------------------------------------------*\
  #SMART CHECK CARDS CLIENT
\*----------------------------------------------------------------------------*/

/**
 * Client for fetching Smart Check card validation HTML from the server.
 *
 * @example
 * const client = SmartCheckCardsClient();
 * client.fetch({ userId: '456', siteId: '123' })
 *   .then(html => container.innerHTML = html)
 *   .catch(error => console.error(error));
 */
const SmartCheckCardsClient = () => {
  /**
   * Fetches card validation HTML for a specific user at a specific site.
   *
   * @param {Object} options - The options for the request.
   * @param {string} options.userId - The ID of the user to fetch card data for.
   * @param {string} options.siteId - The ID of the site to validate against.
   * @returns {Promise<string>} The card validation HTML content.
   * @throws {Error} If the request fails.
   */
  const fetch = async ({ userId, siteId }) => {
    const url = new URL(`/users/${userId}/smart_check_cards`, window.location.origin);
    url.searchParams.append('site_id', siteId);

    const response = await window.fetch(url.toString(), {
      method: 'GET',
      headers: {
        Accept: 'text/html',
      },
      credentials: 'same-origin',
    });

    if (!response.ok) {
      const errorHtml = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(errorHtml, 'text/html');
      const errorText = doc.body.textContent.trim();
      const error = new Error(errorText || 'Failed to fetch card data');
      error.status = response.status;
      throw error;
    }

    return response.text();
  };

  return {
    fetch,
  };
};

export default SmartCheckCardsClient;
